import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { DateTime } from 'luxon';
import PatientService from '../../services/patientService';
import AppointmentService from '../../services/appointmentService';
import PaymentService from '../../services/paymentService';
import TreatmentPlanService from '../../services/treatmentPlanService';
import DentalNoteService from '../../services/dentalNoteService';
import LaboratoryOrderService from '../../services/laboratoryOrderService';
import OrganizationService from '../../services/organizationService';
import DiagnosisService from '../../services/diagnosisService';
import ProfileService from '../../services/profileService';
import { getRegimenFiscalByCode, getUsoCfdiByCode } from '../../utils/satCatalogs';
import { generatePatientRecordPDF } from '../../utils/pdfGenerator';
import { mergeQuestionnaire } from '../../utils/questionnaireUtils';
import { useUserAutoCreation } from '../../hooks/useUserAutoCreation';
import { useOrganizationTimezone } from '../../hooks/useOrganizationTimezone';
import { useAuthSync } from '../../hooks/useAuthSync';
import Button from '../common/Button';
import { formatDateEs, calculateAge } from '../../utils';
import { formatPhoneForWhatsApp } from '../../utils/phoneUtils';
import {
  STATUS_LABELS,
  formatLaboratoryDate,
  getDisplayStatus
} from '../laboratory/laboratoryOrderUtils';
import './PatientDetails.css';

const PatientDetails = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const { userProfile } = useUserAutoCreation();
  const { timezone } = useOrganizationTimezone();
  const { hasPermission, getAppRole, isMedicalDataEnabled } = useAuthSync();
  const appRole = getAppRole();
  const isNonUser = appRole === 'admin' || appRole === 'owner';
  const canViewMedicalData = hasPermission('patient:viewmedicaldata');
  const canViewContactDetails = hasPermission('patient:viewcontactdetails');
  const [patient, setPatient] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [lastAppointment, setLastAppointment] = useState(null);
  const [nextAppointment, setNextAppointment] = useState(null);
  const [openLaboratoryOrder, setOpenLaboratoryOrder] = useState(null);
  const [lastPayment, setLastPayment] = useState(null);
  const [totalBalance, setTotalBalance] = useState(0);
  const [totalActiveTreatmentCost, setTotalActiveTreatmentCost] = useState(0);
  const [pendingBalance, setPendingBalance] = useState(0);
  const [isLoadingAdditionalData, setIsLoadingAdditionalData] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasBlockPhiTag, setHasBlockPhiTag] = useState(false);
  // Clinical features are hidden (not just locked) when the organization has medical data disabled
  const hideClinicalFeatures = hasBlockPhiTag || !isMedicalDataEnabled();
  const [isLaboratoryEnabled, setIsLaboratoryEnabled] = useState(true);
  const [isOrganizationLoaded, setIsOrganizationLoaded] = useState(false);
  const [organization, setOrganization] = useState(null);
  const latestAdditionalDataRequestRef = useRef(0);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(Number(amount || 0));
  };


  const fetchPatient = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = auth.user?.access_token;
      const patientData = await PatientService.getPatientById(patientId, token);
      setPatient(patientData);
    } catch (err) {
      setError('El paciente ha sido eliminado o no existe.');
    } finally {
      setIsLoading(false);
    }
  }, [patientId, auth.user?.access_token]);

  const fetchAdditionalData = useCallback(async () => {
    if (!patientId || !auth.user?.access_token) return;

    const requestId = latestAdditionalDataRequestRef.current + 1;
    latestAdditionalDataRequestRef.current = requestId;
    
    try {
      setIsLoadingAdditionalData(true);
      const token = auth.user.access_token;

      // Fetch appointments, payments, treatment plans, and laboratory orders in parallel.
      const [appointmentsResult, paymentsResult, treatmentPlansResult, laboratoryOrdersResult] = await Promise.allSettled([
        AppointmentService.getAppointmentsByPatient(patientId, token),
        PaymentService.listByPatient(patientId, token, 100),
        TreatmentPlanService.getTreatmentPlansByPatient(patientId, token),
        isOrganizationLoaded && isLaboratoryEnabled
          ? LaboratoryOrderService.getOrdersByPatientAllPages(patientId, token)
          : Promise.resolve([])
      ]);

      // Process appointments
      if (latestAdditionalDataRequestRef.current !== requestId) {
        return;
      }

      if (appointmentsResult.status === 'fulfilled' && appointmentsResult.value) {
        const appointments = appointmentsResult.value;
        if (appointments && appointments.length > 0) {
          // Sort by date and get the most recent completed appointment
          const completedAppointments = appointments
            .filter(apt => apt.status === 'completed')
            .sort((a, b) => new Date(b.appointmentDateTime) - new Date(a.appointmentDateTime));
          
          if (completedAppointments.length > 0) {
            setLastAppointment(completedAppointments[0]);
          }

          const userTimezone = timezone || 'America/Mexico_City';
          const now = DateTime.now().setZone(userTimezone);
          const upcomingAppointments = appointments
            .filter(apt => apt.appointmentDateTime)
            .filter(apt => !['cancelled', 'completed', 'no-show'].includes(apt.status))
            .map(apt => ({
              appointment: apt,
              dateTime: DateTime.fromISO(apt.appointmentDateTime).setZone(userTimezone),
              endDateTime: DateTime.fromISO(apt.appointmentDateTime)
                .setZone(userTimezone)
                .plus({ minutes: Number(apt.durationMinutes) || 30 })
            }))
            .filter(apt => apt.dateTime.isValid && apt.endDateTime.isValid && apt.endDateTime >= now)
            .sort((a, b) => a.dateTime.toMillis() - b.dateTime.toMillis());

          setNextAppointment(upcomingAppointments[0]?.appointment || null);
        } else {
          setNextAppointment(null);
        }
      }

      // Process payments
      if (paymentsResult.status === 'fulfilled' && paymentsResult.value) {
        const paymentsData = paymentsResult.value;
        if (paymentsData && paymentsData.data && paymentsData.data.length > 0) {
          const payments = paymentsData.data;

          // Sort by effective payment date and get the most recent
          const sortedPayments = payments.sort((a, b) => new Date(b.paymentDate || b.createdAt) - new Date(a.paymentDate || a.createdAt));
          setLastPayment(sortedPayments[0]);
          
          // Calculate net total balance (payments minus refunds)
          const total = payments.reduce((sum, payment) => {
            const paymentAmount = parseFloat(payment.amount) || 0;
            const refundAmount = parseFloat(payment.refundAmount) || 0;
            return sum + (paymentAmount - refundAmount);
          }, 0);
          setTotalBalance(total);
        } else {
          setLastPayment(null);
          setTotalBalance(0);
        }
      }

      // Process treatment plans
      if (treatmentPlansResult.status === 'fulfilled' && treatmentPlansResult.value) {
        const treatmentPlansData = treatmentPlansResult.value;
        if (treatmentPlansData && treatmentPlansData.data && treatmentPlansData.data.length > 0) {
          const treatmentPlans = treatmentPlansData.data;
          
          // Calculate total cost for active treatment plans
          const activeStatuses = ['accepted', 'in_progress', 'paused'];
          const activePlans = treatmentPlans.filter(plan => {
            const normalizedStatus = plan.status === 'active' ? 'quoted' : plan.status;
            return activeStatuses.includes(normalizedStatus);
          });
          const activePlansTotal = activePlans.reduce((sum, plan) => {
            return sum + (parseFloat(plan.totalCost) || 0);
          }, 0);
          setTotalActiveTreatmentCost(activePlansTotal);
        } else {
          setTotalActiveTreatmentCost(0);
        }
      }

      if (laboratoryOrdersResult.status === 'fulfilled' && Array.isArray(laboratoryOrdersResult.value)) {
        const userTimezone = timezone || 'America/Mexico_City';
        const openOrders = laboratoryOrdersResult.value
          .filter((order) => {
            const displayStatus = getDisplayStatus(order, userTimezone);
            return displayStatus !== 'received_in_clinic' && displayStatus !== 'cancelled';
          })
          .sort((leftOrder, rightOrder) => {
            const leftDate = leftOrder.requestedDeliveryDate || '9999-12-31';
            const rightDate = rightOrder.requestedDeliveryDate || '9999-12-31';

            if (leftDate !== rightDate) {
              return leftDate.localeCompare(rightDate);
            }

            return new Date(rightOrder.updatedAt || rightOrder.createdAt || 0)
              - new Date(leftOrder.updatedAt || leftOrder.createdAt || 0);
          });

        setOpenLaboratoryOrder(openOrders[0] || null);
      } else {
        setOpenLaboratoryOrder(null);
      }

    } catch (err) {
      console.error('Error fetching additional patient data:', err);
    } finally {
      if (latestAdditionalDataRequestRef.current === requestId) {
        setIsLoadingAdditionalData(false);
      }
    }
  }, [patientId, auth.user?.access_token, isLaboratoryEnabled, isOrganizationLoaded, timezone]);

  useEffect(() => {
    if (patientId && auth.user?.access_token) {
      fetchPatient();
    }
  }, [patientId, auth.user?.access_token, fetchPatient]);

  useEffect(() => {
    if (patientId && auth.user?.access_token) {
      fetchAdditionalData();
    }
  }, [patientId, auth.user?.access_token, fetchAdditionalData]);

  // Load organization and check for block_phi tag
  useEffect(() => {
    const loadOrganization = async () => {
      try {
        const token = auth.user?.access_token;
        const userId = auth.user?.profile?.sub;
        if (!token || !userId) return;
        const org = await OrganizationService.getCurrentUserOrganization(userId, token);
        setOrganization(org);
        const tags = org?.tags || [];
        setHasBlockPhiTag(Array.isArray(tags) && tags.includes('block_phi'));
        setIsLaboratoryEnabled(true);
      } catch (e) {
        // If organization cannot be loaded, default to no block
        setHasBlockPhiTag(false);
        setIsLaboratoryEnabled(true);
      } finally {
        setIsOrganizationLoaded(true);
      }
    };
    loadOrganization();
  }, [auth.user?.access_token, auth.user?.profile?.sub]);

  // Calculate pending balance whenever totals change
  useEffect(() => {
    const pending = totalActiveTreatmentCost - totalBalance;
    setPendingBalance(pending);
  }, [totalActiveTreatmentCost, totalBalance]);

  const getGenderLabel = (gender) => {
    const genderMap = {
      'male': 'Masculino',
      'female': 'Femenino',
      'other': 'Otro',
      'prefer-not-to-say': 'Prefiero no decir'
    };
    return genderMap[gender] || 'No especificado';
  };

  const handleCall = () => {
    if (patient?.cellPhone || patient?.phoneNumber) {
      const phone = patient.cellPhone || patient.phoneNumber;
      window.open(`tel:${phone}`, '_self');
    }
  };

  const handleWhatsApp = () => {
    if (patient?.cellPhone || patient?.phoneNumber) {
      const phone = patient.cellPhone || patient.phoneNumber;
      // Format phone number for WhatsApp
      const formattedPhone = formatPhoneForWhatsApp(phone);
      // Remove the '+' prefix for wa.me URL (wa.me expects digits only)
      const whatsappNumber = formattedPhone.replace(/^\+/, '');
      // Use organization's WhatsApp message template if available, otherwise use default
      const messageTemplate = organization?.whatsappDefaultMessage || 'Hola {nombre}, te contacto desde la clínica dental.';
      // Replace all {nombre} placeholders with actual patient name
      const message = messageTemplate.replace(/{nombre}/g, patient.firstName || '');
      const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
    }
  };

  const handleMessage = () => {
    if (patient?.email) {
      window.open(`mailto:${patient.email}`, '_self');
    }
  };

  const handleDownloadRecord = async () => {
    if (!patient || !auth.user?.access_token) {
      alert('No se puede generar el expediente. Por favor, intenta de nuevo.');
      return;
    }

    try {
      setIsGeneratingPDF(true);
      const token = auth.user.access_token;
      const userId = auth.user?.profile?.sub;

      // Load clinic information
      let clinic = null;
      try {
        clinic = await OrganizationService.getCurrentUserOrganization(userId, token);
      } catch (clinicError) {
        console.warn('Could not load clinic information:', clinicError);
      }

      // Get user information - try to load from API if userProfile is not available
      let userProfileData = userProfile;
      if (!userProfileData || !userProfileData.name) {
        try {
          userProfileData = await ProfileService.getUserProfile(userId, token);
        } catch (profileError) {
          console.warn('Could not load user profile:', profileError);
        }
      }

      // Build user name from available fields
      let userName = '';
      if (userProfileData) {
        if (userProfileData.name) {
          userName = userProfileData.name;
        } else if (userProfileData.firstName && userProfileData.lastName) {
          userName = `${userProfileData.firstName} ${userProfileData.lastName}`;
        } else if (userProfileData.firstName) {
          userName = userProfileData.firstName;
        } else if (auth.user?.profile?.name) {
          userName = auth.user.profile.name;
        }
      } else if (auth.user?.profile?.name) {
        userName = auth.user.profile.name;
      }

      // Get user information
      const userInfo = {
        name: userName || 'Dr./Dra. _______________',
        professionalId: userProfileData?.professionalId || userProfileData?.licenseNumber || '',
        email: userProfileData?.email || auth.user?.profile?.email || '',
        licenseId: userProfileData?.licenseId || ''
      };

      // Get patient data with medical history
      const patientData = await PatientService.getPatientById(patientId, token);
      
      // Convert HTML medical history to text
      const htmlToText = (html) => {
        if (!html) return '';
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
      };
      
      const medicalHistory = htmlToText(patientData?.medicalHistory || '');
      const questionnaire = mergeQuestionnaire(patientData?.medicalQuestionnaire);
      const physicalExam = patientData?.physicalExam || null;
      const vitalSigns = patientData?.vitalSigns || null;
      const systemsReview = patientData?.systemsReview || null;

      // Get dental notes to find initial odontogram
      let initialDentalNote = null;
      try {
        const notesResponse = await DentalNoteService.getDentalNotesByPatient(
          patientId,
          { status: 'active', limit: 100 },
          token
        );
        
        const notesData = notesResponse.data || [];
        
        if (notesData.length > 0) {
          // Sort by creation date (oldest first) to get the initial odontogram
          const sortedNotes = [...notesData].sort((a, b) => {
            const dateA = new Date(a.createdAt || a.created_at || 0);
            const dateB = new Date(b.createdAt || b.created_at || 0);
            return dateA - dateB;
          });
          
          initialDentalNote = sortedNotes[0];
          
          // Load diagnosis information for the initial note
          if (initialDentalNote) {
            const diagnosisIds = new Set();
            
            // Collect diagnosis IDs from the note
            if (initialDentalNote.subNotes && initialDentalNote.subNotes.length > 0) {
              initialDentalNote.subNotes.forEach(subNote => {
                if (subNote.diagnosisId) {
                  diagnosisIds.add(subNote.diagnosisId);
                }
              });
            } else if (initialDentalNote.diagnosisId) {
              diagnosisIds.add(initialDentalNote.diagnosisId);
            }
            
            // Load diagnoses
            let diagnosisCache = new Map();
            if (diagnosisIds.size > 0) {
              diagnosisCache = await DiagnosisService.getDiagnosesMap(token).catch(error => {
                console.error('Error loading diagnoses:', error);
                return new Map();
              });
            }
            
            // Attach diagnosis info to the note
            if (initialDentalNote.subNotes && initialDentalNote.subNotes.length > 0) {
              initialDentalNote.subNotes = initialDentalNote.subNotes.map(subNote => {
                const diagnosis = diagnosisCache.get(subNote.diagnosisId);
                return {
                  ...subNote,
                  diagnosisName: diagnosis?.name || 'Diagnóstico no encontrado',
                  diagnosisColor: diagnosis?.color || '#gray',
                  isCompleteTooth: diagnosis?.isCompleteTooth || false
                };
              });
            } else if (initialDentalNote.diagnosisId) {
              const diagnosis = diagnosisCache.get(initialDentalNote.diagnosisId);
              initialDentalNote.diagnosisName = diagnosis?.name || 'Diagnóstico no encontrado';
              initialDentalNote.diagnosisColor = diagnosis?.color || '#gray';
              initialDentalNote.isCompleteTooth = diagnosis?.isCompleteTooth || false;
            }
          }
        }
      } catch (notesError) {
        console.warn('Could not load dental notes:', notesError);
        // Continue without odontogram if notes can't be loaded
      }

      // Generate PDF
      await generatePatientRecordPDF(
        patientData,
        medicalHistory,
        clinic,
        userInfo,
        questionnaire,
        physicalExam,
        vitalSigns,
        systemsReview,
        initialDentalNote,
        clinic?.logoViewUrl
      );
    } catch (error) {
      console.error('Error generating patient record PDF:', error);
      alert('Error al generar el PDF del expediente. Por favor, inténtalo de nuevo.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const getNextAppointmentDateLabel = () => {
    if (!nextAppointment?.appointmentDateTime) return 'No especificado';
    const userTimezone = timezone || 'America/Mexico_City';
    const appointmentDate = DateTime.fromISO(nextAppointment.appointmentDateTime).setZone(userTimezone);
    if (!appointmentDate.isValid) return 'No especificado';

    const dateLabel = formatDateEs(appointmentDate.toISODate());
    const today = DateTime.now().setZone(userTimezone).startOf('day');
    const appointmentDay = appointmentDate.startOf('day');
    const diffDays = Math.round(appointmentDay.diff(today, 'days').days);

    if (diffDays === 0) return `${dateLabel} • Hoy`;
    if (diffDays === 1) return `${dateLabel} • en 1 dia`;
    if (diffDays > 1) return `${dateLabel} • en ${diffDays} dias`;
    return `${dateLabel} • hace ${Math.abs(diffDays)} dias`;
  };

  const getNextAppointmentDoctorName = () => {
    if (!nextAppointment) return 'No especificado';
    const doctor = nextAppointment.doctor || nextAppointment.user;
    if (!doctor) return 'No especificado';
    if (doctor.name) return doctor.name;
    const fullName = `${doctor.firstName || ''} ${doctor.lastName || ''}`.trim();
    return fullName || doctor.email || 'No especificado';
  };

  const getOpenLaboratoryOrderStatusLabel = () => {
    if (!openLaboratoryOrder) return 'No especificado';

    const displayStatus = getDisplayStatus(openLaboratoryOrder, timezone || 'America/Mexico_City');
    return STATUS_LABELS[displayStatus] || 'No especificado';
  };

  const handleOpenLaboratoryOrder = () => {
    if (!openLaboratoryOrder?.id) return;
    navigate(`/laboratory-orders/${openLaboratoryOrder.id}/edit`);
  };

  const handleManageLaboratory = () => {
    navigate(`/laboratory-orders?patientId=${patient.id}`, {
      state: {
        patientName: `${patient.firstName} ${patient.lastName}`.trim()
      }
    });
  };

  const handleScheduleAppointment = () => {
    navigate(`/appointments/new?patientId=${patient.id}`);
  };

  const handleArchiveClick = () => {
    setShowDeleteModal(true);
  };

  const handleCloseArchiveModal = () => {
    setShowDeleteModal(false);
  };

  const handleConfirmArchive = async () => {
    if (!patient || !auth.user?.access_token) {
      return;
    }

    try {
      setIsDeleting(true);
      await PatientService.deletePatient(patient.id, auth.user.access_token);
      
      // Redirect to patients list after successful archiving
      navigate('/patients/list');
    } catch (error) {
      console.error('Error archiving patient:', error);
      alert('Error al archivar el paciente. Por favor, intenta de nuevo.');
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="patient-details-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="patient-details-error">
        <div className="error-content">
          <i className="fas fa-exclamation-triangle"></i>
          <h3>Error al cargar el paciente</h3>
          <p>{error}</p>
          <Button variant="primary" onClick={() => navigate('/patients/list')}>
            Volver a la lista
          </Button>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="patient-details-not-found">
        <div className="not-found-content">
          <i className="fas fa-user-slash"></i>
          <h3>Paciente no encontrado</h3>
          <p>El paciente que buscas no existe o ha sido eliminado.</p>
          <Button variant="primary" onClick={() => navigate('/patients/list')}>
            Volver a la lista
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="patient-details-container">
      {/* Botón de regresar y editar */}
      <div
        className="patient-back-button-section"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <Button
          variant="outline"
          onClick={() => navigate("/patients/list")}
          icon="fas fa-arrow-left"
        >
          Regresar
        </Button>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button
            variant="outline"
            onClick={() => navigate(`/patients/${patient.id}/edit`)}
            icon="fas fa-edit"
          >
            Editar
          </Button>
          <Button
            variant="outline"
            onClick={handleArchiveClick}
            icon="fas fa-archive"
            className="btn-archive-outline"
          >
            Archivar
          </Button>
        </div>
      </div>

      {/* Header con foto y nombre */}
      <div className="patient-header">
        <div className="patient-avatar">
          <i className="fas fa-user"></i>
        </div>
        <div className="patient-info">
          <h1>
            {patient.firstName} {patient.lastName}
          </h1>
          <p className="patient-subtitle">
            {getGenderLabel(patient.gender)} •{" "}
            {formatDateEs(patient.dateOfBirth)} •{" "}
            {calculateAge(patient.dateOfBirth)}
          </p>
          <p className="patient-subtitle">
            {isLoadingAdditionalData ? (
              "Cargando información adicional..."
            ) : (
              <>
                {lastAppointment ? (
                  <>
                    Último procedimiento:{" "}
                    {formatDateEs(lastAppointment.appointmentDateTime)} •{" "}
                  </>
                ) : (
                  "Sin procedimientos • "
                )}
                {lastPayment ? (
                  <>
                    Último pago: {formatCurrency(lastPayment.amount)} (
                    {formatDateEs(lastPayment.createdAt)}) •{" "}
                  </>
                ) : (
                  "Sin pagos • "
                )}
                Saldo pendiente: 
                <span
                  style={{
                    fontWeight: "bold",
                    color:
                      pendingBalance > 0
                        ? "#e74c3c"
                        : pendingBalance < 0
                        ? "#27ae60"
                        : "#34495e",
                  }}
                >
                  {formatCurrency(pendingBalance)}
                </span>
              </>
            )}
          </p>
          <p>
            {!hideClinicalFeatures && (patient.medicalQuestionnaire?.pathological?.alergiaAnestesicos
              ?.answer ||
              patient.medicalQuestionnaire?.pathological?.alergiaAntibioticos
                ?.answer ||
              patient.alergias) && (
              <span className="highlight-allergies">
                <i
                  className="fas fa-exclamation-triangle"
                  style={{ color: "red", marginRight: 6 }}
                ></i>
                <strong>Alergias conocidas:</strong>{" "}
                {[
                  patient.medicalQuestionnaire?.pathological?.alergiaAnestesicos
                    ?.answer
                    ? patient.medicalQuestionnaire.pathological
                        .alergiaAnestesicos.details
                    : "",
                  patient.medicalQuestionnaire?.pathological
                    ?.alergiaAntibioticos?.answer
                    ? patient.medicalQuestionnaire.pathological
                        .alergiaAntibioticos.details
                    : "",
                  patient.alergias ? patient.alergias : "",
                ]
                  .filter(Boolean)
                  .join(", ")}
              </span>
            )}
          </p>
        </div>
      </div>

      {nextAppointment && (
        <div className="next-appointment-banner">
          <div className="next-appointment-header">
            <i className="fas fa-calendar-check"></i>
            <span>Proxima cita</span>
          </div>
          <div className="next-appointment-grid patient-alert-grid">
            <div className="next-appointment-item">
              <span className="next-appointment-label">Fecha</span>
              <span className="next-appointment-value">{getNextAppointmentDateLabel()}</span>
            </div>
            <div className="next-appointment-item">
              <span className="next-appointment-label">Procedimiento</span>
              <span className="next-appointment-value">
                {nextAppointment.procedure || 'No especificado'}
              </span>
            </div>
            <div className="next-appointment-item">
              <span className="next-appointment-label">Doctor</span>
              <span className="next-appointment-value">{getNextAppointmentDoctorName()}</span>
            </div>
            <div className="next-appointment-item full-width">
              <span className="next-appointment-label">Notas</span>
              <span className="next-appointment-value">
                {nextAppointment.notes || 'Sin notas'}
              </span>
            </div>
          </div>
        </div>
      )}

      {openLaboratoryOrder && (
        <div
          className="next-appointment-banner patient-alert-banner"
          onClick={handleOpenLaboratoryOrder}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleOpenLaboratoryOrder();
            }
          }}
          role="button"
          tabIndex={0}
        >
          <div className="next-appointment-header">
            <i className="fas fa-flask"></i>
            <span>Orden de laboratorio abierta</span>
          </div>
          <div className="next-appointment-grid patient-alert-grid">
            <div className="next-appointment-item">
              <span className="next-appointment-label">Fecha de entrega</span>
              <span className="next-appointment-value">
                {formatLaboratoryDate(openLaboratoryOrder.requestedDeliveryDate, timezone || 'America/Mexico_City')}
              </span>
            </div>
            <div className="next-appointment-item">
              <span className="next-appointment-label">Trabajo</span>
              <span className="next-appointment-value">
                {openLaboratoryOrder.workType || 'No especificado'}
              </span>
            </div>
            <div className="next-appointment-item">
              <span className="next-appointment-label">Estado</span>
              <span className="next-appointment-value">{getOpenLaboratoryOrderStatusLabel()}</span>
            </div>
            <div className="next-appointment-item">
              <span className="next-appointment-label">Proveedor</span>
              <span className="next-appointment-value">
                {openLaboratoryOrder.providerName || 'No especificado'}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="action-buttons-section">
        <div className="action-buttons-grid">
          <Button variant="primary" onClick={handleScheduleAppointment}>
            <i className="fas fa-calendar-plus"></i>
            Agendar Cita
          </Button>
          <Button 
            variant="outline" 
            onClick={handleCall}
            disabled={!patient?.cellPhone && !patient?.phoneNumber}
            style={!canViewContactDetails ? { display: 'none' } : {}}
          >
            <i className="fas fa-phone"></i>
            Llamar
          </Button>
          <Button 
            variant="outline" 
            onClick={handleWhatsApp}
            disabled={!patient?.cellPhone && !patient?.phoneNumber}
            style={!canViewContactDetails ? { display: 'none' } : {}}
          >
            <i className="fab fa-whatsapp"></i>
            WhatsApp
          </Button>
          <Button 
            variant="outline" 
            onClick={handleMessage}
            disabled={!patient?.email}
            style={!canViewContactDetails ? { display: 'none' } : {}}
          >
            <i className="fas fa-envelope"></i>
            Mensaje
          </Button>
          {!hideClinicalFeatures && (
            <Button variant="outline" onClick={handleDownloadRecord} disabled={isGeneratingPDF}>
              <i className="fas fa-download"></i>
              {isGeneratingPDF ? 'Generando...' : 'Descargar Expediente'}
            </Button>
          )}
        </div>
      </div>

      {/* Información del paciente */}
      <div className="patient-information">
        <div className="collapsible-section">
          <div
            className="collapsible-header"
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            <h2>Información del Paciente</h2>
            <i className={`fas fa-chevron-${isCollapsed ? "down" : "up"}`}></i>
          </div>
          <div className={`collapsible-content ${!isCollapsed ? "open" : ""}`}>
            <div className="info-sections">
              <div className="info-section">
                <h3>Información Personal</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Nombre</label>
                    <span>{patient.firstName || "No especificado"}</span>
                  </div>
                  <div className="info-item">
                    <label>Apellidos</label>
                    <span>{patient.lastName || "No especificado"}</span>
                  </div>
                  {canViewContactDetails && (
                    <div className="info-item">
                      <label>Correo Electrónico</label>
                      <span>{patient.email || "-"}</span>
                    </div>
                  )}
                  <div className="info-item">
                    <label>Género</label>
                    <span>{getGenderLabel(patient.gender)}</span>
                  </div>
                  <div className="info-item">
                    <label>Fecha de Nacimiento</label>
                    <span>{formatDateEs(patient.dateOfBirth)}</span>
                  </div>
                  {canViewContactDetails && (
                    <div className="info-item">
                      <label>Celular</label>
                      <span>
                        {patient.cellPhone ||
                          patient.phoneNumber ||
                          "-"}
                      </span>
                    </div>
                  )}
                  <div className="info-item">
                    <label>Ocupación</label>
                    <span>{patient.occupation || "No especificado"}</span>
                  </div>
                  <div className="info-item">
                    <label>Estado Civil</label>
                    <span>{patient.estadoCivil || "No especificado"}</span>
                  </div>
                  <div className="info-item">
                    <label>Lengua</label>
                    <span>{patient.lengua || "No especificado"}</span>
                  </div>
                  {!hideClinicalFeatures && (
                    <div className="info-item">
                      <label>Tipo de Sangre</label>
                      <span>{patient.tipoSangre || "No especificado"}</span>
                    </div>
                  )}
                </div>
              </div>

              {!hideClinicalFeatures && patient.alergias && (
                <div className="info-section">
                  <h3>Alergias</h3>
                  <div className="notes-content">
                    <p>{patient.alergias}</p>
                  </div>
                </div>
              )}

              <div className="info-section">
                <h3>Dirección</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Calle</label>
                    <span>{patient.address?.street || "No especificado"}</span>
                  </div>
                  <div className="info-item">
                    <label>Ciudad</label>
                    <span>{patient.address?.city || "No especificado"}</span>
                  </div>
                  <div className="info-item">
                    <label>Estado</label>
                    <span>{patient.address?.state || "No especificado"}</span>
                  </div>
                  <div className="info-item">
                    <label>Código Postal</label>
                    <span>{patient.address?.zipCode || "No especificado"}</span>
                  </div>
                  <div className="info-item">
                    <label>País</label>
                    <span>{patient.address?.country || "No especificado"}</span>
                  </div>
                </div>
              </div>

              <div className="info-section">
                <h3>Datos de Facturación</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Nombre del Receptor</label>
                    <span>
                      {patient.facturacion?.nombreReceptor || "No especificado"}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>RFC</label>
                    <span>
                      {patient.facturacion?.rfc || "No especificado"}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Código Postal</label>
                    <span>
                      {patient.facturacion?.codigoPostal || "No especificado"}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Régimen Fiscal</label>
                    <span>
                      {patient.facturacion?.regimenFiscal ? (
                        <>
                          {patient.facturacion.regimenFiscal} -{" "}
                          {getRegimenFiscalByCode(
                            patient.facturacion.regimenFiscal
                          )?.descripcion || "Descripción no encontrada"}
                        </>
                      ) : (
                        "No especificado"
                      )}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Uso de CFDI</label>
                    <span>
                      {patient.facturacion?.usoCfdi ? (
                        <>
                          {patient.facturacion.usoCfdi} -{" "}
                          {getUsoCfdiByCode(patient.facturacion.usoCfdi)
                            ?.descripcion || "Descripción no encontrada"}
                        </>
                      ) : (
                        "No especificado"
                      )}
                    </span>
                  </div>
                </div>
              </div>

              <div className="info-section">
                <h3>Contacto de Emergencia</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Nombre del Contacto</label>
                    <span>
                      {patient.emergencyContact?.name || "No especificado"}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Teléfono del Contacto</label>
                    <span>
                      {patient.emergencyContact?.phoneNumber ||
                        "No especificado"}
                    </span>
                  </div>
                </div>
              </div>

              {patient.notes && (
                <div className="info-section">
                  <h3>Notas Adicionales</h3>
                  <div className="notes-content">
                    <p>{patient.notes}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sección de funcionalidades del paciente */}
      <div className="patient-features-section">
        <h2>Gestión del Paciente</h2>
        <div className="features-grid">
          {!hideClinicalFeatures && (
            <div
              className={`feature-card${!canViewMedicalData ? ' disabled' : ''}`}
              onClick={canViewMedicalData ? () => navigate(`/patients/${patient.id}/medical-history`) : undefined}
              style={{ cursor: canViewMedicalData ? "pointer" : "not-allowed" }}
            >
              <div className="feature-icon">
                <i className="fas fa-history"></i>
              </div>
              <div className="feature-content">
                <h3>Historia clínica</h3>
                <p>Ver y editar historia clínica del paciente</p>
              </div>
              <i className={`fas ${canViewMedicalData ? 'fa-chevron-right' : 'fa-lock'} feature-arrow`}></i>
            </div>
          )}

          {/*
          <div className="feature-card" onClick={() => navigate(`/patients/${patient.id}/dental-chart`)} style={{cursor:'pointer'}}>
            <div className="feature-icon">
              <i className="fas fa-tooth"></i>
            </div>
            <div className="feature-content">
              <h3>Cartilla Dental</h3>
              <p>Visualizar cartilla dental del paciente</p>
            </div>
            <i className="fas fa-chevron-right feature-arrow"></i>
          </div>
          */}

          {!hideClinicalFeatures && (
            <>
              {/* Odontograma */}
              <div
                className={`feature-card${!canViewMedicalData ? ' disabled' : ''}`}
                onClick={canViewMedicalData ? () => navigate(`/patients/${patient.id}/dental-notes`) : undefined}
                style={{ cursor: canViewMedicalData ? "pointer" : "not-allowed" }}
              >
                <div className="feature-icon">
                  <i className="fas fa-sticky-note"></i>
                </div>
                <div className="feature-content">
                  <h3>Odontograma</h3>
                  <p>Gestionar odontograma inicial, evolución y altas</p>
                </div>
                <i className={`fas ${canViewMedicalData ? 'fa-chevron-right' : 'fa-lock'} feature-arrow`}></i>
              </div>

              {/* Legacy dental notes/odontogram view - COMENTADO para posible rollback
              <div
                className="feature-card"
                onClick={() => navigate(`/patients/${patient.id}/dental-notes/legacy`)}
                style={{ cursor: "pointer" }}
              >
                <div className="feature-icon">
                  <i className="fas fa-sticky-note"></i>
                </div>
                <div className="feature-content">
                  <h3>Odontograma (Anterior)</h3>
                  <p>Ver y gestionar notas dentales anteriores</p>
                </div>
                <i className="fas fa-chevron-right feature-arrow"></i>
              </div>
              */}
            </>
          )}

          {/* <div className="feature-card" onClick={() => navigate(`/patients/${patient.id}/certificates`)} style={{cursor:'pointer'}}>
            <div className="feature-icon">
              <i className="fas fa-certificate"></i>
            </div>
            <div className="feature-content">
              <h3>Certificado Dental</h3>
              <p>Emitir certificados dentales</p>
            </div>
            <i className="fas fa-chevron-right feature-arrow"></i>
          </div> */}

          {!hideClinicalFeatures && (
            <div
              className={`feature-card${!canViewMedicalData ? ' disabled' : ''}`}
              onClick={canViewMedicalData ? () => navigate(`/patients/${patient.id}/prescriptions`) : undefined}
              style={{ cursor: canViewMedicalData ? "pointer" : "not-allowed" }}
            >
              <div className="feature-icon">
                <i className="fas fa-pills"></i>
              </div>
              <div className="feature-content">
                <h3>Prescripción</h3>
                <p>Gestionar prescripciones médicas</p>
              </div>
              <i className={`fas ${canViewMedicalData ? 'fa-chevron-right' : 'fa-lock'} feature-arrow`}></i>
            </div>
          )}

          {!hideClinicalFeatures && (
            <div
              className={`feature-card${!canViewMedicalData ? ' disabled' : ''}`}
              onClick={canViewMedicalData ? () => navigate(`/patients/${patient.id}/evolution-notes`) : undefined}
              style={{ cursor: canViewMedicalData ? "pointer" : "not-allowed" }}
            >
              <div className="feature-icon">
                <i className="fas fa-notes-medical"></i>
              </div>
              <div className="feature-content">
                <h3>Notas de Evolución</h3>
                <p>Registro cronológico de evolución clínica</p>
              </div>
              <i className={`fas ${canViewMedicalData ? 'fa-chevron-right' : 'fa-lock'} feature-arrow`}></i>
            </div>
          )}

          <div
            className="feature-card"
            onClick={() => navigate(`/patients/${patient.id}/balance`)}
            style={{ cursor: "pointer" }}
          >
            <div className="feature-icon">
              <i className="fas fa-balance-scale"></i>
            </div>
            <div className="feature-content">
              <h3>Balance</h3>
              <p>Ver resumen de planes activos y pagos</p>
            </div>
            <i className="fas fa-chevron-right feature-arrow"></i>
          </div>

          <div
            className="feature-card"
            onClick={() => navigate(`/patients/${patient.id}/treatment-plans`)}
            style={{ cursor: "pointer" }}
          >
            <div className="feature-icon">
              <i className="fas fa-clipboard-list"></i>
            </div>
            <div className="feature-content">
              <h3>Planes de Tratamiento</h3>
              <p>Gestionar planes de tratamiento dental</p>
            </div>
            <i className="fas fa-chevron-right feature-arrow"></i>
          </div>

          {isLaboratoryEnabled && isNonUser && (
            <div
              className="feature-card"
              onClick={handleManageLaboratory}
              style={{ cursor: 'pointer' }}
            >
              <div className="feature-icon">
                <i className="fas fa-flask"></i>
              </div>
              <div className="feature-content">
                <h3>Laboratorio Dental</h3>
                <p>Ver ordenes de laboratorio del paciente</p>
              </div>
              <i className="fas fa-chevron-right feature-arrow"></i>
            </div>
          )}

          <div
            className="feature-card"
            onClick={() => navigate(`/patients/${patient.id}/payments`)}
            style={{ cursor: "pointer" }}
          >
            <div className="feature-icon">
              <i className="fas fa-credit-card"></i>
            </div>
            <div className="feature-content">
              <h3>Pagos</h3>
              <p>Gestionar pagos del paciente</p>
            </div>
            <i className="fas fa-chevron-right feature-arrow"></i>
          </div>

          <div
            className="feature-card"
            onClick={() => navigate(`/patients/${patient.id}/appointments`)}
            style={{ cursor: "pointer" }}
          >
            <div className="feature-icon">
              <i className="fas fa-calendar-alt"></i>
            </div>
            <div className="feature-content">
              <h3>Agenda de Citas</h3>
              <p>Ver agenda de citas del paciente</p>
            </div>
            <i className="fas fa-chevron-right feature-arrow"></i>
          </div>

          {!hideClinicalFeatures && (
            <div
              className={`feature-card${!canViewMedicalData ? ' disabled' : ''}`}
              onClick={canViewMedicalData ? () => navigate(`/patients/${patient.id}/photos`) : undefined}
              style={{ cursor: canViewMedicalData ? "pointer" : "not-allowed" }}
            >
              <div className="feature-icon">
                <i className="fas fa-camera"></i>
              </div>
              <div className="feature-content">
                <h3>Fotos del Paciente</h3>
                <p>Gestionar fotos del paciente</p>
              </div>
              <i className={`fas ${canViewMedicalData ? 'fa-chevron-right' : 'fa-lock'} feature-arrow`}></i>
            </div>
          )}

        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal delete-modal">
            <div className="modal-header">
              <h3>Confirmar archivado</h3>
              <button className="modal-close" onClick={handleCloseArchiveModal}>
                ✕
              </button>
            </div>
            <div className="modal-content">
              <p>
                ¿Estás seguro de que deseas archivar al paciente{' '}
                <strong>"{patient.firstName} {patient.lastName}"</strong>?
              </p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleCloseArchiveModal}
                disabled={isDeleting}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={handleConfirmArchive}
                disabled={isDeleting}
              >
                {isDeleting ? 'Archivando...' : 'Archivar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientDetails;